import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Moon, Star, Eye, Sparkles, Flower2, Feather } from "lucide-react";
import api from "../api";
import ServiceCard from "../components/ServiceCard";
import { usePageSeo } from "../components/common";
import { NewsletterSignup } from "../components/NewsletterSignup";

const HERO_KEYWORDS = ["TAROT", "INTUITION", "ÉNERGÉTIQUE", "SPIRITUALITÉ", "DIEU", "ET UN PEU DE MAGIE"];

const VALUES = [
  { icon: Star, t: "TAROT", s: "Cartomancie intuitive" },
  { icon: Sparkles, t: "ÉNERGÉTIQUE", s: "Lecture des énergies" },
  { icon: Eye, t: "INTROSPECTION", s: "Comprendre et avancer" },
  { icon: Moon, t: "SPIRITUALITÉ", s: "Un chemin entre toi et Dieu" },
  { icon: Flower2, t: "FÉMINITÉ SACRÉE", s: "Reconnexion à toi" },
  { icon: Feather, t: "LIBRE ARBITRE", s: "Tu restes au centre" },
];

function DarkSectionTitle({ eyebrow, title, sub }) {
  return (
    <div className="max-w-2xl">
      {eyebrow && <div className="eyebrow mb-3 text-[#DFC07B]">{eyebrow}</div>}
      <h2 className="home-section-title text-3xl sm:text-4xl lg:text-[2.75rem] font-medium leading-[1.15]">{title}</h2>
      {sub && <p className="home-sub mt-4 text-base sm:text-lg leading-relaxed">{sub}</p>}
      <div className="home-divider mt-6" />
    </div>
  );
}

export default function Home() {
  usePageSeo({
    title: "Tirages intuitifs & cartomancie à distance | Noëmie",
    description: "Découvre les tirages intuitifs à distance de Noëmie, ta sorcière préférée : cartes, intuition et ressentis pour éclairer ton présent. Aucun appel, aucune visio.",
    path: "/",
  });
  const [guidances, setGuidances] = useState([]);
  const [energetique, setEnergetique] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/services?category=guidance").then((r) => setGuidances(r.data)).catch(() => {});
    api.get("/services?category=energetique").then((r) => setEnergetique(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="home-dark">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="home-hero-glow" />
        <div className="container-x relative z-[1] py-14 sm:py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6 fade-up">
              <div className="eyebrow mb-5 flex items-center gap-2 text-[#DFC07B]">
                <Sparkles size={14} /> La Maison de ta Sorcière Préférée
              </div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium text-[#F9F5EE] leading-[1.02]">
                Noëmie,
              </h1>
              <p className="font-script text-4xl sm:text-5xl lg:text-6xl text-[#DFC07B] mt-1 mb-7">ta sorcière préférée</p>
              <p className="text-[#E7DCEA] text-base sm:text-lg leading-relaxed max-w-lg">
                {settings.signature_phrase ||
                  "Je ne te prédis pas ton avenir. Je t'aide à éclairer ton présent, comprendre ce qui se joue et écouter ce que ton intuition essaie de te dire."}
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/guidances" data-testid="hero-cta-guidances" className="btn-primary">
                  Découvrir mes tirages intuitifs <ArrowRight size={16} />
                </Link>
                <Link to="/energetique" data-testid="hero-cta-seance" className="btn-ghost-gold">
                  Choisir ma séance
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 relative fade-up-2">
              <div className="home-portrait-wrap mx-auto max-w-lg">
                <img
                  src="/noemie-writing.png"
                  alt="Noëmie, cartomancienne intuitive, à la lueur des bougies"
                  className="home-portrait max-h-[78vh]"
                  data-testid="hero-photo"
                />
                <div className="hidden sm:block absolute -right-3 top-8 home-vert-keywords">
                  {HERO_KEYWORDS.join("   ·   ")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 EDITORIAL CARDS */}
      <section className="container-x py-14 sm:py-20">
        <div className="grid gap-5 lg:grid-cols-3">
          {/* Newsletter — Le Tirage de la Semaine */}
          <div className="home-card-dark fade-up" data-testid="home-card-newsletter">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="font-serif text-2xl text-[#F9F5EE]">Le Tirage de la Semaine</h3>
              <span className="text-xs rounded-full px-3 py-1 bg-[#D4AF37]/20 text-[#DFC07B] shrink-0">Gratuit</span>
            </div>
            <p className="text-sm text-[#CBB9CF] leading-relaxed flex-1">
              Chaque semaine, reçois par email mon tirage intuitif de la semaine : l'énergie du moment,
              ce qui mérite ton attention et le message qui ressort des cartes.
            </p>
            <a href="#newsletter" className="btn-primary mt-6" data-testid="home-card-newsletter-cta">
              Je reçois mon tirage ✨
            </a>
          </div>

          {/* Entre Lui et Moi */}
          <div className="home-card-dark home-card-bordeaux fade-up-2" data-testid="home-card-courrier">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="font-serif text-2xl text-[#F9F5EE]">Entre Lui et Moi</h3>
              <span className="text-xs rounded-full px-3 py-1 bg-[#D4AF37]/20 text-[#DFC07B] shrink-0">Courrier mensuel</span>
            </div>
            <p className="text-sm text-[#CBB9CF] leading-relaxed">Un courrier rien que pour toi, entre ton cœur, Dieu et moi.</p>
            <ul className="mt-4 space-y-1.5 text-sm text-[#E7DCEA] flex-1">
              {["Un verset de la Bible", "Une guidance du mois selon ton signe", "Un petit mot personnel", "Un sticker & une attention papier"].map((b) => (
                <li key={b} className="flex items-start gap-2"><span className="text-[#DFC07B]">✦</span>{b}</li>
              ))}
            </ul>
            <Link to="/entre-lui-et-moi" className="btn-ghost-gold mt-6" data-testid="home-card-courrier-cta">
              Découvrir Entre Lui et Moi
            </Link>
          </div>

          {/* Guide Tarot */}
          <div className="home-card-dark fade-up-3 relative" data-testid="home-card-guide">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="font-serif text-2xl text-[#F9F5EE] pr-20">Le Grimoire Visuel du Tarot</h3>
              <span className="absolute top-6 right-6 inline-flex flex-col items-center rounded-full px-3 py-1.5 bg-[#D4AF37] text-[#12051F] text-sm font-bold leading-tight text-center">
                14,90€<span className="text-[10px] font-medium">au lieu de 19,90€</span>
              </span>
            </div>
            <p className="text-sm text-[#CBB9CF] leading-relaxed">Arrête d'apprendre le Tarot. Commence à le lire. Le Tarot de Marseille, enfin clair.</p>
            <ul className="mt-4 space-y-1.5 text-sm text-[#E7DCEA] flex-1">
              {["78 fiches A4 illustrées (une par carte)", "+ 9 fiches pratiques", "+ un Kit Intuitif de 13 bonus"].map((b) => (
                <li key={b} className="flex items-start gap-2"><span className="text-[#DFC07B]">✦</span>{b}</li>
              ))}
            </ul>
            <Link to="/guide-du-tarot" className="btn-primary mt-6" data-testid="home-card-guide-cta">
              Découvrir le guide
            </Link>
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="border-y border-[#D4AF37]/15">
        <div className="container-x py-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.t} className="text-center" data-testid={`value-${v.t.toLowerCase().replace(/[^a-z]/g, "")}`}>
                  <Icon className="mx-auto text-[#DFC07B]" size={26} strokeWidth={1.4} />
                  <p className="mt-3 text-xs tracking-[0.18em] font-semibold text-[#F9F5EE]">{v.t}</p>
                  <p className="mt-1 text-xs text-[#B5A4BA]">{v.s}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MOI C'EST NOËMIE */}
      <section className="container-x py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="grid grid-cols-2 gap-5 sm:gap-7">
            <div className="home-polaroid rotate-[-3deg]"><span className="tape" /><img src="/noemie-tarot.png" alt="Noëmie avec sa tasse à la lune" /></div>
            <div className="home-polaroid rotate-[3deg] mt-10"><span className="tape" /><img src="/noemie-hero.png" alt="Noëmie écrivant son carnet lors d'un tirage" /></div>
          </div>
          <div>
            <div className="eyebrow mb-3 text-[#DFC07B]">Rencontre</div>
            <h2 className="home-section-title text-3xl sm:text-4xl">Moi, c'est Noëmie</h2>
            <p className="mt-5 text-[#E7DCEA] leading-relaxed">
              Je suis cartomancienne intuitive, énergéticienne et profondément connectée au monde invisible.
              À travers mes tirages, mes ressentis et la lecture des cartes, je t'ouvre un espace doux et
              authentique pour t'écouter, te comprendre et avancer, à ton rythme, avec plus de clarté et de confiance.
            </p>
            <p className="mt-5 font-script text-2xl sm:text-3xl text-[#DFC07B]">
              Des cartes, beaucoup d'intuition, un peu de magie et surtout beaucoup d'amour.
            </p>
            <Link to="/a-propos" className="btn-primary mt-8" data-testid="home-about-cta">
              En savoir plus sur mon univers <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* MES TIRAGES INTUITIFS */}
      <section className="container-x pb-8 sm:pb-16">
        <DarkSectionTitle
          eyebrow="Les prestations"
          title="Mes tirages intuitifs"
          sub="Un temps réservé rien que pour toi, à travers mes ressentis, la lecture des cartes et mes oracles."
        />
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {guidances.map((s) => <ServiceCard key={s.slug} service={s} />)}
        </div>
      </section>

      {/* L'ÉNERGÉTIQUE */}
      <section className="border-t border-[#D4AF37]/15">
        <div className="container-x py-16 sm:py-24">
          <DarkSectionTitle
            eyebrow="Bien-être & introspection"
            title="L'Énergétique"
            sub="Des séances énergétiques réalisées à distance dans une approche intuitive, spirituelle et tournée vers le bien-être."
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {energetique.map((s) => <ServiceCard key={s.slug} service={s} />)}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <div id="newsletter"><NewsletterSignup /></div>
    </div>
  );
}
